import type {
  DefinitionComponent,
  BlockReference,
  BlockComponent,
  ObjectReference,
  DefinitionComponentWithKind,
  DefinitionComponentWithoutKind,
  Referenceable
} from "../survey";

export const ReferenceableBlocks = [
  'Attachment',
  'Explanation',
  'Select',
  'Validated',
  'Computed',
  'ApplicantCreator'
] as const;

export const ReferenceableObjects = [
  'ReviewConfiguration',
  'Template Substitution',
  'Template Substitutions',
  'Template Iteration',
  'Template Iterations',
] as const;

export type ReferenceBlocks = `${typeof ReferenceableBlocks[number]} Reference`;
export type ReferenceObjects = `${typeof ReferenceableObjects[number]} Reference`;

export const referenceBlocksArray: ReferenceBlocks[] = ReferenceableBlocks.map(b => `${b} Reference` as const);
export const referenceObjectsArray: ReferenceObjects[] = ReferenceableObjects.map(o => `${o} Reference` as const);

function isObjectReference(ref: BlockReference | ObjectReference): ref is ObjectReference {
  return (referenceObjectsArray as readonly string[]).includes(ref.kind);
}

export function replaceReferenceWithDefinition(
  reference: BlockReference | ObjectReference,
  definition: DefinitionComponent,
): (Referenceable & { id: string }) | Object | null {
  // First check that the definition is the same kind as the reference and that the ids match
  // New format
  if (definition.kind === 'Definition') {
    if (reference.kind !== (definition.component.kind + ' Reference') || reference.definitionId !== definition.id) {
      console.error("Reference", reference, "is not the same kind as the definition", definition);
      return null;
    }

    // Object Reference: replace with inline config (no BlockComponent, no id)
    if (isObjectReference(reference)) {
      // Clone: definition.component may be a frozen/shared object from extractDefinitions.
      return structuredClone(definition.component);
    }

    // Only BlockReferences remain (referenceBlocksArray kinds)
    const blockDefinition = definition.component;
    const { kind: _kind, definitionId: _definitionId, ...referenceProps } = reference;
    return structuredClone({
      ...blockDefinition,
      ...referenceProps
    } satisfies Referenceable & { id: string });

  // Legacy format
  } else {
    if (reference.kind !== (definition.kind + ' Reference') || reference.definitionId !== definition.definitionProps.id) {
      console.error("Reference", reference, "is not the same kind as the definition", definition);
      return null;
    }

    // Object Reference: replace with inline config (no BlockComponent, no id)
    if (isObjectReference(reference)) {
      const { definitionProps: _definitionProps, ...inlineConfig } = definition;
      return structuredClone(inlineConfig);
    }

    // Only BlockReferences remain (referenceBlocksArray kinds)
    const blockDefinition = definition;
    const { kind: _kind, definitionId: _definitionId, ...referenceProps } = reference;
    return structuredClone({
      ...blockDefinition,
      ...referenceProps
    } satisfies Referenceable & { id: string });
  }
}
